import {
  AccountBean,
  AdGroupListItemBean, DealBean,
  CampaignBean,
  ItemResponseAccountBeanObject,
} from 'common/api/api'
import type { DataNode } from 'antd/es/tree'
import {accountApi} from 'common/api/entitiesApi'
import {User} from 'common/context/UserContext'
import moment from 'moment'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import RoleEnum = AccountBean.RoleEnum
import FormatTypesEnum = DealBean.FormatTypesEnum;
import { StorageKey } from 'common/context/Storage.tsx'

interface IabCategory {
  id?: number
  parentId?: number | null
  name?: string
  code?: string
}

interface TreeSelectDataNode {
  title: string
  key: string
  value: string
  children?: TreeSelectDataNode[]
}

interface MenuItem {
  key: string
}

interface NavigatorWithMS extends Navigator {
  msSaveOrOpenBlob?: (blob: Blob, filename: string) => void
  msSaveBlob?: (blob: Blob, filename: string) => void
}


dayjs.extend(utc);
dayjs.extend(timezone);

export function downloadCSVData(fileName: string, csvData: Blob): void {
  const nav = window.navigator as NavigatorWithMS
  if (nav.msSaveOrOpenBlob) {
    nav.msSaveBlob?.(csvData, fileName)
  } else {
    const a = window.document.createElement('a')
    a.href = URL.createObjectURL(csvData)
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }
}

export function menuItem(path: string, child: React.ReactNode, icon?: React.ReactNode): React.ReactElement {
  return (
    <Link to={`/${path}`}>
      {icon && <span className={`${icon} menuLeft__icon`} />}
      <span className="menuLeft__item-title">
        {child}
      </span>
    </Link>
  )
}

export function isAuthenticated(): boolean {
  const isAuth = localStorage.getItem(StorageKey.Authenticated) === 'true'
  if (!isAuth) return false

  const expirationString = localStorage.getItem(StorageKey.TokenExpiration)
  if (!expirationString) return false

  const expiration = Number(expirationString)
  return expiration > Date.now()
}

export function isAdmin(user?: User): boolean {
  return user?.role === RoleEnum.Admin
}

export function isPublisher(user: User): boolean {
  return user.role === RoleEnum.Publisher
}

export function isMenuItemAllowedForUser(user?: User, menu?: MenuItem): boolean {
  if (!user?.role) {
    return false
  }

  const blockedMenuKeysAdvertiser: string[] = [
    'adv',
    'admin',
    'supply',
    'conversion-report',
    'dsp-report',
    'dsp',
    'dsp-endpoint',
  ]

  const blockedMenuKeysPublisher: string[] = [
    'admin',
    'event-report',
    'dsp-report',
    'conversion-report',
    'publisher-report',
    'ssp',
    'deals',
    'demand',
  ]

  if (user.role === RoleEnum.Admin) {
    return true
  }

  if (user.role === RoleEnum.Publisher) {
    return menu ? !blockedMenuKeysPublisher.includes(menu.key) : false
  }

  return menu ? !blockedMenuKeysAdvertiser.includes(menu.key) : false
}

export function setLoggedUserProps(
  setUser: (
    id: number | undefined,
    name: string | undefined,
    email: string | undefined,
    role: RoleEnum | undefined,
  ) => void,
): void {
  accountApi.accountProps().then((item: ItemResponseAccountBeanObject) => {
    if (item.data) {
      setUser(item.data.id, item.data.name, item.data.email, item.data.role)
    }
  })
}

export function getTreeData(list: { name: string }[]) {
  const namesList: string[] = []
  list.forEach((value) => namesList.push(value.name))
  const root: DataNode[] = []

  namesList.forEach((item, index) => {
    const groupName = item.split(' ')[0]
    const group = root.find((node) => String(node.key) === String(groupName + '_group'))

    if (group && group.children) {
      group.children.push({
        title: item,
        key: item,
      })
    } else {
      const itemNext = namesList[index + 1]

      if (itemNext) {
        const groupNameNext = itemNext.split(' ')[0]

        if (groupName === groupNameNext) {
          const newGroup: DataNode = {
            title: groupName,
            key: groupName + '_group',
            children: [
              {
                title: item,
                key: item,
              },
            ],
          }
          root.push(newGroup)
        } else {
          root.push({
            title: item,
            key: item,
            children: [],
          })
        }
      }
    }
  })
  return root
}

export function getIabCategoriesAsTree(list: IabCategory[]): TreeSelectDataNode[] {
  const root: TreeSelectDataNode[] = []

  // Filter categories with valid data
  const validCategories = list.filter((v) => v.id != null && v.name != null)

  // Add parent categories
  validCategories
    .filter((v) => v.parentId === null)
    .forEach((v) =>
      root.push({
        title: v.name!,
        key: String(v.id!),
        value: String(v.id!),
        children: [],
      }),
    )

  // Add child categories
  validCategories
    .filter((v) => v.parentId !== null)
    .forEach((v) => {
      const group = root.find((item) => String(item.key) === String(v.parentId))
      if (group && group.children) {
        group.children.push({
          title: v.name!,
          key: String(v.id!),
          value: String(v.id!),
        })
      }
    })
  return root
}

export function sorterFunc<T>(getData: (item: T) => any) {
  return (a: T, b: T) => {
    const dataA: any = getData(a) ?? ''
    const dataB: any = getData(b) ?? ''

    if (typeof dataA === 'number' && typeof dataB === 'number') {
      return dataA - dataB
    }
    return String(dataA).localeCompare(String(dataB))
  }
}

export function getFilterData<T>(arr: T[] | undefined, getData: (value: T) => string) {
  if (!arr) return []
  const filterData: { text: string; value: string }[] = []
  arr.forEach((value) =>
    filterData.push({
      text: getData(value),
      value: getData(value),
    }),
  )
  return filterData.filter((v, i, a) => a.findIndex((t) => t.text === v.text && t.value === v.value) === i)
}

export function filterFunc<T>(getData: (record: T) => string) {
  return (value: string, record: T) => {
    return getData(record).toString().localeCompare(value) === 0
  }
}

export function clone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime()) as unknown as T
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => clone(item)) as unknown as T
  }

  if (obj instanceof Object) {
    const copy = {} as T
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        copy[key] = clone(obj[key])
      }
    }
    return copy
  }

  throw new Error("Unable to copy obj! Its type isn't supported.")
}

export function normalizeArrayFields(fieldNames: string[], form: any): void {
  fieldNames.forEach((name) => {
    if (form.isFieldTouched(name)) {
      const value = form.getFieldValue(name)
      if (Array.isArray(value)) {
        form.setFieldsValue({ [name]: value.filter(Boolean) })
      }
    }
  })
}

export function getParentId(
  childId: any,
  childList: any[],
  parentField: (item: any) => number | undefined,
): number | null {
  const child = childList.find((item) => item.id === childId)
  if (!child) return null
  const parentId = parentField(child)
  return parentId ?? null
}

export function calcDatesPercent(bean: AdGroupListItemBean | CampaignBean): number {
  const startDate = moment(bean.startDate)
  const endDate = moment(bean.endDate)
  const now = moment()

  if (now.isBefore(startDate)) return 0
  if (now.isAfter(endDate)) return 100

  const total = endDate.diff(startDate)
  const current = now.diff(startDate)
  return Math.round((current / total) * 100)
}

export function useActionsMediaQuery(breakpoint: string): boolean {
  const [matches, setMatches] = useState(false)

  useEffect(() => {
    const mediaQuery = window.matchMedia(breakpoint)
    setMatches(mediaQuery.matches)

    const handler = (event: MediaQueryListEvent) => setMatches(event.matches)
    mediaQuery.addListener(handler)

    return () => mediaQuery.removeListener(handler)
  }, [breakpoint])

  return matches
}

export const withActionsMediaQuery = (breakpoint: string) => (WrappedComponent: React.ComponentType<any>) => {
  return class extends React.Component<any> {
    state = {
      matches: false,
    }

    private mediaQuery!: MediaQueryList

    componentDidMount() {
      this.mediaQuery = window.matchMedia(breakpoint)
      this.setState({ matches: this.mediaQuery.matches })
      this.mediaQuery.addListener(this.handleActionsMediaQuery)
    }

    componentWillUnmount() {
      this.mediaQuery.removeListener(this.handleActionsMediaQuery)
    }

    handleActionsMediaQuery = (event: MediaQueryListEvent) => {
      this.setState({ matches: event.matches })
    }

    render() {
      return <WrappedComponent {...this.props} matches={this.state.matches} />
    }
  }
}

export const lowerize = (object: Record<string, any>) => Object.keys(object).reduce<Record<string, any>>((acc, k) => {
  // eslint-disable-next-line no-param-reassign
  acc[k.toLowerCase()] = object[k]
  return acc
}, {})



export function toTimezone(date: Dayjs, timezoneId: string | undefined): Dayjs {
  return date.tz(timezoneId);
}

export const formatNumber = (num: number) => {
  return num
    .toLocaleString('ru-RU', {
      maximumFractionDigits: 2
    })
    .replace(/\s/g, '.');
  // .replace(',', '.');
}

export const adFormatNames = {
  [FormatTypesEnum.Pop]: 'Pop-under',
  [FormatTypesEnum.Push]: 'Push Notification',
  [FormatTypesEnum.Banner]: 'Banner',
  [FormatTypesEnum.Native]: 'Native',
  [FormatTypesEnum.Video]: 'Video',
};
